import {
  TYPE,
  useParkingStatisticsRpt,
  useParkingStatisticsRptExport,
} from '@services/report'
import Flex from '@shared/Flex'
import { Button } from '@shared/Form'
import useSearch from '@shared/innmaxLib/hooks/useSearch'
import { rules } from '@shared/innmaxLib/utils/form'
import Layout from '@shared/Layout'
import Table from '@shared/Table'
import {
  convertTimestampToDuration,
  downloadFile,
  isNilAndToThousandSeparator,
} from '@shared/utils/webHelper'
import { Form } from 'antd'
import { ColumnsType } from 'antd/es/table'
import moment from 'moment'
import { isNil } from 'ramda'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FormItem } from 'src/components/Form'
import OptionsSelect from 'src/components/OptionsSelect'
import { StatusBox } from '../style'

const initialReportType = {
  reportType: TYPE.YEAR,
  conditionDate: new Date().getFullYear(),
  createStartAt: moment().startOf('year').valueOf(),
  createEndAt: moment().endOf('year').valueOf(),
  pageSize: 999999999,
}

function ParkingStatistics() {
  const { t } = useTranslation()

  const [form] = Form.useForm()
  const [currentType, setCurrentType] = React.useState(TYPE.YEAR)
  const [isSearch, setIsSearch] = React.useState(false)

  const { query, handleSearch, handleTableChange } = useSearch({
    form,
    initial: initialReportType,
    dateRangeField: ['startAt', 'endAt'],
  })

  const handleReportType = (val: any) => {
    setCurrentType(val?.reportType)
    return val
  }

  const [exporting, handleExport] = useParkingStatisticsRptExport(query, {
    onSuccess: (blob: any) => {
      downloadFile({
        blob,
        filename: 'ParkingReport',
      })
    },
  })

  const { dataSource, loading } = useParkingStatisticsRpt(query)

  const columns: ColumnsType<any> = [
    {
      title: '縣市',
      dataIndex: 'cityName',
      align: 'center',
    },
    {
      title: '行政區',
      dataIndex: 'locationNs',
      align: 'center',
    },
    {
      title: '停車費 收益排名',
      dataIndex: 'rank',
      align: 'center',
      sorter: Table.Sorter({
        sortField: 'rank',
      }),
    },
    {
      title: '站點',
      dataIndex: 'sessionName',
      align: 'center',
    },
    {
      title: '停車費合計',
      dataIndex: 'parkingFee',
      align: 'center',
      render: v => isNilAndToThousandSeparator(v),
      sorter: Table.Sorter({
        sortField: 'parkingFee',
      }),
    },
    {
      title: '納管車格數',
      dataIndex: 'parkingSpaceCount',
      align: 'center',
      render: v => isNilAndToThousandSeparator(v, '格'),
    },
    {
      title: '可開單時數',
      dataIndex: 'availableTime',
      align: 'center',
      render: v => convertTimestampToDuration(v),
    },
    {
      title: '開單數',
      dataIndex: 'orderCount',
      align: 'center',
      render: v => isNilAndToThousandSeparator(v, '張'),
    },
    {
      title: '廢單數',
      dataIndex: 'cancelCount',
      align: 'center',
      render: v => isNilAndToThousandSeparator(v, '張'),
    },
    {
      title: '加簽數',
      dataIndex: 'signCount',
      align: 'center',
      render: v => isNilAndToThousandSeparator(v, '次'),
    },
    {
      title: '計時金額合計',
      dataIndex: 'timeFee',
      align: 'center',
      render: v => isNilAndToThousandSeparator(v),
    },
    {
      title: '計次金額合計',
      dataIndex: 'countFee',
      align: 'center',
      render: v => isNilAndToThousandSeparator(v),
    },
    {
      title: '累進金額合計',
      dataIndex: 'progressiveFee',
      align: 'center',
      render: v => isNilAndToThousandSeparator(v),
    },
    {
      title: '使用率',
      dataIndex: 'usageRate',
      align: 'center',
      render: v => isNilAndToThousandSeparator(v, '%'),
    },
    {
      title: '週轉率',
      dataIndex: 'turnoverRate',
      align: 'center',
      render: v => isNilAndToThousandSeparator(v, '%'),
    },
  ]

  const renderStatusContent = () => {
    const data: any = dataSource
    return (
      <div className="flex">
        <StatusBox
          title="納管車格合計"
          unit="格"
          data={data}
          dataKey="parkingSpaceCount"
        />
        <StatusBox
          title="可開單時數合計"
          unit="時"
          data={data}
          dataKey="availableDuration"
        />
        <StatusBox title="開單數" unit="張" data={data} dataKey="orderCount" />
        <StatusBox title="廢單數" unit="張" data={data} dataKey="cancelCount" />
        <StatusBox title="加簽數" unit="次" data={data} dataKey="signCount" />
        <StatusBox title="使用率" unit="%" data={data} dataKey="usageRate" />
        <StatusBox title="週轉率" unit="%" data={data} dataKey="turnoverRate" />
        <StatusBox
          title="停車費合計"
          unit="NTD"
          data={data}
          dataKey="parkingFee"
          detail={[
            {
              title: '計時',
              dataKey: 'timeFee',
            },
            {
              title: '計次',
              dataKey: 'countFee',
            },
            {
              title: '累進',
              dataKey: 'progressiveFee',
            },
          ]}
        />
      </div>
    )
  }

  React.useEffect(() => {
    if (!isNil(query) && !isNil(query.reportType) && !isNil(query.sessionId)) {
      setIsSearch(true)
    }
  }, [query])

  return (
    <>
      <Layout>
        <Form layout="inline" form={form}>
          <FormItem
            label={'縣市/行政區'}
            name="sessionId"
            requiredMark="optional"
            rules={[rules.required]}
            className="w-[280px]">
            <OptionsSelect.CityAreaSelect
              className="w-130"
              supportAll={false}
            />
          </FormItem>

          <FormItem
            label={`${t('report:report type')} / ${t('report:time')}`}
            name="reportTime"
            initialValue={initialReportType}
            className={currentType === TYPE.DAY ? 'w-[380px]' : 'w-[250px]'}
            getValueFromEvent={handleReportType}>
            <OptionsSelect.ReportRangeTimeSelect />
          </FormItem>

          <FormItem.Button>
            <Button
              loading={false}
              type="primary"
              className="w-96"
              onClick={handleSearch}>
              {t('common:search')}
            </Button>
          </FormItem.Button>
          <FormItem.Button>
            <Button
              loading={exporting}
              type="primary"
              ghost
              className="w-96"
              onClick={handleExport}>
              {t('report:export')}
            </Button>
          </FormItem.Button>
        </Form>

        {renderStatusContent()}
        {isSearch ? (
          <>
            <div className="mt-44 mb-16">
              <Flex between>
                <Table.RowCount count={dataSource?.content?.length || 0} />
              </Flex>
            </div>
            <Table
              simpleMode
              columns={columns}
              loading={loading}
              dataSource={dataSource || []}
              onChange={handleTableChange}
              pagination={false}
            />
          </>
        ) : (
          <div
            className="flex justify-center items-center text-center text-lg"
            style={{ color: '#8E8E8E', height: '50dvh', width: '50dvw' }}>
            <span>請先輸入搜尋條件範圍</span>
          </div>
        )}
      </Layout>
    </>
  )
}

export default React.memo(ParkingStatistics)
